// src/components/Sidebar.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Plus, LogOut, User, ChevronUp, Folder, ShoppingBag, Pencil, Trash2, Banknote, Building2, Building, Lightbulb, Search, SlidersHorizontal, Target, Settings, Shield } from 'lucide-react';
import CreateProjectModal, { ProjectCreatedPayload } from './CreateProjectModal';
import EditProjectModal from './EditProjectModal';
import GatheringInfoToast from './GatheringInfoToast';
import SettingsModal from './SettingsModal';
import { gatheringInfoNoticeMessage } from '../utils/projectNotices';
import { useCaps } from '../contexts/CapabilitiesContext';
import { getCookie } from '../utils/utils';

interface Project {
  id: number;
  name: string;
  goal?: string;
  category?: string;
  score: number;
  owner_email: string;
  is_owner: boolean;
  member_count: number;
  created_at: string;
  team?: number | null;
  team_name?: string | null;
  lifecycle?: 'draft' | 'active';
}

interface TeamFilter {
  id: number;
  name: string;
}

interface UserData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  profile_picture: string | null;
  org?: number | null;
  is_catalyx_user?: boolean;
  is_superuser?: boolean;
  is_staff?: boolean;
  is_free_tier?: boolean;
  is_free_user?: boolean;
}

interface SidebarProps {
  // --- REFACTORED: Removed activeItem ---
  activeProject?: number;
  activeNav?: string;
  onNavigate?: (itemId: string) => void;
  onProjectSelect?: (projectId: number) => void;
  onProjectContinue?: (project: ProjectCreatedPayload) => void;
  onLogout?: () => void;
  userData?: UserData | null;
  selectedCurrency?: string;
  onCurrencyChange?: (currency: string) => void;
  projectsRefreshTrigger?: number; // Increment this to trigger sidebar refresh
  onProjectsChanged?: () => void;
  // Shared filter state (if provided, use it; otherwise use internal state)
  projectSearch?: string;
  setProjectSearch?: (value: string) => void;
  selectedTeamFilter?: number | null;
  setSelectedTeamFilter?: (value: number | null) => void;
  selectedMarketFilter?: string;
  setSelectedMarketFilter?: (value: string) => void;
  selectedTerritoryFilter?: string;
  setSelectedTerritoryFilter?: (value: string) => void;
  selectedCategoryFilter?: string;
  setSelectedCategoryFilter?: (value: string) => void;
  selectedOccasionFilter?: string;
  setSelectedOccasionFilter?: (value: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  // --- REFACTORED: Removed activeItem default ---
  activeProject,
  activeNav,
  onNavigate, 
  onProjectSelect,
  onProjectContinue,
  onLogout,
  userData,
  selectedCurrency = 'CHF',
  onCurrencyChange,
  projectsRefreshTrigger,
  onProjectsChanged,
  // Shared filter state (use props if provided, otherwise internal state)
  projectSearch: propProjectSearch,
  setProjectSearch: propSetProjectSearch,
  selectedTeamFilter: propSelectedTeamFilter,
  setSelectedTeamFilter: propSetSelectedTeamFilter,
  selectedMarketFilter: propSelectedMarketFilter,
  setSelectedMarketFilter: propSetSelectedMarketFilter,
  selectedTerritoryFilter: propSelectedTerritoryFilter,
  setSelectedTerritoryFilter: propSetSelectedTerritoryFilter,
  selectedCategoryFilter: propSelectedCategoryFilter,
  setSelectedCategoryFilter: propSetSelectedCategoryFilter,
  selectedOccasionFilter: propSelectedOccasionFilter,
  setSelectedOccasionFilter: propSetSelectedOccasionFilter,
}) => {
  const { can, allCapabilities } = useCaps();
  const canOpenAdminPanel =
    can('admin_panel') ||
    allCapabilities ||
    Boolean(userData?.is_superuser || userData?.is_staff);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [gatheringInfoNotice, setGatheringInfoNotice] = useState<string | null>(null);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  // Delete Confirmation State
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; project: Project | null }>({
    show: false,
    project: null
  });
  const [deleting, setDeleting] = useState(false);
  const [projectActionError, setProjectActionError] = useState<string | null>(null);

  // Team filter state
  const [teamFilters, setTeamFilters] = useState<TeamFilter[]>([]);
  
  // Use shared filter state if provided, otherwise use internal state
  const [internalProjectSearch, setInternalProjectSearch] = useState('');
  const [internalSelectedTeamFilter, setInternalSelectedTeamFilter] = useState<number | null>(null);
  const [internalSelectedMarketFilter, setInternalSelectedMarketFilter] = useState<string>('');
  const [internalSelectedTerritoryFilter, setInternalSelectedTerritoryFilter] = useState<string>('');
  const [internalSelectedCategoryFilter, setInternalSelectedCategoryFilter] = useState<string>('');
  const [internalSelectedOccasionFilter, setInternalSelectedOccasionFilter] = useState<string>('');

  const projectSearch = propProjectSearch !== undefined ? propProjectSearch : internalProjectSearch;
  const setProjectSearch = propSetProjectSearch || setInternalProjectSearch;
  const selectedTeamFilter = propSelectedTeamFilter !== undefined ? propSelectedTeamFilter : internalSelectedTeamFilter;
  const setSelectedTeamFilter = propSetSelectedTeamFilter || setInternalSelectedTeamFilter;
  const selectedMarketFilter = propSelectedMarketFilter !== undefined ? propSelectedMarketFilter : internalSelectedMarketFilter;
  const setSelectedMarketFilter = propSetSelectedMarketFilter || setInternalSelectedMarketFilter;
  const selectedTerritoryFilter = propSelectedTerritoryFilter !== undefined ? propSelectedTerritoryFilter : internalSelectedTerritoryFilter;
  const setSelectedTerritoryFilter = propSetSelectedTerritoryFilter || setInternalSelectedTerritoryFilter;
  const selectedCategoryFilter = propSelectedCategoryFilter !== undefined ? propSelectedCategoryFilter : internalSelectedCategoryFilter;
  const setSelectedCategoryFilter = propSetSelectedCategoryFilter || setInternalSelectedCategoryFilter;
  const selectedOccasionFilter = propSelectedOccasionFilter !== undefined ? propSelectedOccasionFilter : internalSelectedOccasionFilter;
  const setSelectedOccasionFilter = propSetSelectedOccasionFilter || setInternalSelectedOccasionFilter;

  const [filterOptions, setFilterOptions] = useState<{ markets: string[]; territory_demand_space: string[]; categories: string[]; occasions: string[] }>({ markets: [], territory_demand_space: [], categories: [], occasions: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [settingsModalSection, setSettingsModalSection] = useState<'currency' | 'organization'>('currency');

  const settingsMenuItems = [
    { 
      id: 'profile', 
      label: 'Profile', 
      icon: User, 
      action: () => {
        if (onNavigate) {
          onNavigate('profile');
        }
      }
    },
    // Currency selector hidden: orgs now have a single billing currency.
    // { 
    //   id: 'currency', 
    //   label: 'Currency', 
    //   icon: Banknote, 
    //   action: () => {
    //     setSettingsModalSection('currency');
    //     setShowSettingsModal(true);
    //     setShowSettingsMenu(false);
    //   }
    // },
    ...(userData?.org && !userData?.is_free_user && can('organizations_user_management_menu')
      ? [{
          id: 'my-organization',
          label: 'My Organization',
          icon: Building,
          action: () => {
            if (onNavigate) onNavigate('organization');
            setShowSettingsMenu(false);
          },
        }]
      : []),
    ...(can('organizations_selection')
      ? [{
          id: 'organization',
          label: 'Organization',
          icon: Building2,
          action: () => {
            setSettingsModalSection('organization');
            setShowSettingsModal(true);
            setShowSettingsMenu(false);
          },
        }]
      : []),
    ...(canOpenAdminPanel
      ? [{
          id: 'admin-panel',
          label: 'Admin Panel',
          icon: Shield,
          action: () => {
            if (onNavigate) onNavigate('admin-panel');
            setShowSettingsMenu(false);
          },
        }]
      : []),
    { 
      id: 'logout', 
      label: 'Logout', 
      icon: LogOut, 
      action: onLogout 
    },
  ];

  // Build query params for project fetching
  const buildProjectParams = () => {
    const params = new URLSearchParams();
    if (selectedTeamFilter) params.set('team_id', String(selectedTeamFilter));
    if (projectSearch.trim()) params.set('search', projectSearch.trim());
    if (selectedMarketFilter) params.set('market', selectedMarketFilter);
    if (selectedTerritoryFilter) params.set('territory_demand_space', selectedTerritoryFilter);
    if (selectedCategoryFilter) params.set('category', selectedCategoryFilter);
    if (selectedOccasionFilter) params.set('occasion', selectedOccasionFilter);
    params.set('lifecycle', 'active');
    return params.toString();
  };

  // Fetch projects from API with search/filter params
  const fetchProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const qs = buildProjectParams();
      const url = `/api/projects/${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(Array.isArray(data) ? data : data.results || []);
      } else {
        console.error('Failed to fetch projects');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchFilterOptions = async (noFilters?: boolean) => {
    try {
      const qs = noFilters ? '' : buildProjectParams();
      const url = `/api/projects/filter-options/${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setFilterOptions(await response.json());
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  // Fetch projects on mount and when org/filters change
  useEffect(() => {
    fetchProjects();
    fetchTeamsForFilter();
    fetchFilterOptions();
  }, [userData?.org]);

  // Refresh projects when refresh trigger changes (e.g., when project is created elsewhere)
  useEffect(() => {
    if (projectsRefreshTrigger !== undefined && projectsRefreshTrigger > 0) {
      fetchProjects();
      fetchFilterOptions();
    }
  }, [projectsRefreshTrigger]);

  // Re-fetch projects and filter options when filters change (except search)
  useEffect(() => {
    fetchProjects();
    fetchFilterOptions();
  }, [selectedTeamFilter, selectedMarketFilter, selectedTerritoryFilter, selectedCategoryFilter, selectedOccasionFilter]);

  // Debounce search input changes and refresh filter options to match results
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchProjects();
      fetchFilterOptions();
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [projectSearch]);

  const fetchTeamsForFilter = async () => {
    try {
      const response = await fetch('/api/teams/?mine=1', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTeamFilters(data);
      }
    } catch (err) {
      console.error('Error fetching teams for filter:', err);
    }
  };

  // Close settings menu when clicking outside
  useEffect(() => {
    // ... (handleClickOutside logic remains the same)
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    };

    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsMenu]);

  const handleProjectCreated = (newProject: ProjectCreatedPayload) => {
    if (newProject.lifecycle === 'draft') {
      fetchFilterOptions(true);
      onProjectsChanged?.();
      return;
    }
    setProjects(prevProjects => [newProject as unknown as Project, ...prevProjects]);
    fetchFilterOptions(true); // refresh filter dropdowns so new project's market/category/etc. appear
    if (onProjectSelect) {
      onProjectSelect(newProject.id);
    }
    const notice = gatheringInfoNoticeMessage(newProject);
    if (notice) setGatheringInfoNotice(notice);
    // Note: projectsRefreshTrigger will also trigger a refresh via useEffect
  };

  const handleEditProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectToEdit(project);
    setShowEditProjectModal(true);
  };

  const handleProjectUpdated = (updatedProject: Project) => {
    setProjects(prevProjects =>
      prevProjects.map(p => p.id === updatedProject.id ? { ...p, ...updatedProject } : p)
    );
  };

  const handleCreateProjectClick = async () => {
    setProjectActionError(null);
    if (can('projects_create')) {
      setShowCreateProjectModal(true);
      return;
    }

    // Force backend capability check to show configured error_text.
    try {
      const csrftoken = getCookie('csrftoken');
      const response = await fetch('/api/projects/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken || '',
        },
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      setProjectActionError(data?.detail || data?.error || 'You do not have permission to create projects.');
    } catch {
      setProjectActionError('You do not have permission to create projects.');
    }
  };

  const handleDeleteClick = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm({ show: true, project });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.project) return;

    setDeleting(true);
    try {
      const csrftoken = getCookie('csrftoken');

      const response = await fetch(`/api/projects/${deleteConfirm.project.id}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken || '',
        },
      });

      if (response.ok) {
        // Remove from local state
        setProjects(prev => prev.filter(p => p.id !== deleteConfirm.project!.id));
        setDeleteConfirm({ show: false, project: null });

        // Navigate away if the deleted project was active
        if (activeProject === deleteConfirm.project.id && onNavigate) {
          onNavigate('');
        }
      } else {
        console.error('Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <aside
        className={`flex-shrink-0 bg-[#655FA8] flex flex-col h-full transition-all duration-300 ease-in-out overflow-hidden z-20 ${
          isExpanded ? 'w-52 shadow-xl' : 'w-12'
        }`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => {
          setIsExpanded(false);
          setShowSettingsMenu(false);
        }}
      >
        {/* Navigation Menu */}
        <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
          <div className="px-2 mb-4">
            <div className="space-y-1">
              {can('blueprints_view') && (
              <button
                onClick={() => onNavigate?.('')}
                className={`group w-full flex items-center rounded-lg text-sm transition-all duration-200 ${
                  isExpanded ? 'gap-2 px-3 py-2.5' : 'justify-center p-2.5'
                } ${
                  activeNav === ''
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                title="Blueprints"
              >
                <Lightbulb className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="truncate">Blueprints</span>}
              </button>
              )}
              {can('marketplace_view') && (
                <button
                  onClick={() => onNavigate?.('marketplace')}
                  className={`group w-full flex items-center rounded-lg text-sm transition-all duration-200 ${
                    isExpanded ? 'gap-2 px-3 py-2.5' : 'justify-center p-2.5'
                  } ${
                    activeNav === 'marketplace'
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  title="Marketplace"
                >
                  <ShoppingBag className="w-5 h-5 flex-shrink-0" />
                  {isExpanded && <span className="truncate">Marketplace</span>}
                </button>
              )}
              {can('gos_view') && (
              <button
                onClick={() => onNavigate?.('outcomes')}
                className={`group w-full flex items-center rounded-lg text-sm transition-all duration-200 ${
                  isExpanded ? 'gap-2 px-3 py-2.5' : 'justify-center p-2.5'
                } ${
                  activeNav === 'outcomes'
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                title="Guaranteed Outputs"
              >
                <Target className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="truncate">GOs</span>}
              </button>
              )}
            </div>
          </div>

          {/* Projects Section — visible only when expanded */}
          {isExpanded && (can('projects_view') || can('projects_create')) && (
          <div className="px-2">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                Projects
              </span>
              <button
                onClick={handleCreateProjectClick}
                className="p-1 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200"
                title="Create new project"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {/* Search */}
            <div className="px-3 pb-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  className="w-full pl-7 pr-7 py-1 text-xs border border-gray-200 rounded-md bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#43C8F5] focus:border-[#43C8F5]"
                  placeholder="Search..."
                />
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded ${showFilters ? 'text-[#43C8F5] bg-[#43C8F5]/10' : 'text-gray-400 hover:text-[#43C8F5]'}`}
                  title="Toggle filters"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Filters panel */}
            {showFilters && (
              <div className="px-3 pb-2 space-y-1.5">
                {/* Team Filter */}
                {teamFilters.length > 1 && (
                  <select
                    value={selectedTeamFilter ?? ''}
                    onChange={(e) => setSelectedTeamFilter(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    <option value="">All Teams</option>
                    {teamFilters.map((team) => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                )}
                {/* Market Filter */}
                {filterOptions.markets.length > 0 && (
                  <select
                    value={selectedMarketFilter}
                    onChange={(e) => setSelectedMarketFilter(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    <option value="">All Markets</option>
                    {filterOptions.markets.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                )}
                {/* Territory Filter */}
                {filterOptions.territory_demand_space.length > 0 && (
                  <select
                    value={selectedTerritoryFilter}
                    onChange={(e) => setSelectedTerritoryFilter(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    <option value="">All Territories</option>
                    {filterOptions.territory_demand_space.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                )}
                {/* Category Filter */}
                {filterOptions.categories.length > 0 && (
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    <option value="">All Categories</option>
                    {filterOptions.categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}
                {/* Occasion Filter */}
                {filterOptions.occasions.length > 0 && (
                  <select
                    value={selectedOccasionFilter}
                    onChange={(e) => setSelectedOccasionFilter(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    <option value="">All Occasions</option>
                    {filterOptions.occasions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {projectActionError && (
              <div className="px-3 pb-1">
                <p className="text-xs text-red-600">{projectActionError}</p>
              </div>
            )}
            
            {isLoadingProjects ? (
              <div className="px-3 py-2">
                <p className="text-xs text-white/50">Loading projects...</p>
              </div>
            ) : projects.length > 0 ? (
              <ul className="mt-2 space-y-1">
                {projects.map((project) => (
                  <li key={project.id}>
                    <button
                      onClick={() => {
                        if (!can('projects_view')) return;
                        onProjectSelect?.(project.id);
                      }}
                      className={`group w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        activeProject === project.id
                          ? 'bg-white/20 text-white'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      title={project.goal || project.name}
                    >
                      <Folder className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate flex-1 text-left">{project.name}</span>
                      {project.is_owner && (
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all duration-200">
                          {can('projects_edit') && (
                            <button
                              onClick={(e) => handleEditProject(project, e)}
                              className="p-1 rounded text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
                              title="Edit project"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDeleteClick(project, e)}
                            className="p-1 rounded text-white/60 hover:text-red-300 hover:bg-red-500/20 transition-all duration-200"
                            title="Delete project"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-2">
                <p className="text-xs text-white/50">No projects yet</p>
                <button
                  onClick={handleCreateProjectClick}
                  className="mt-2 text-xs text-white/80 hover:text-white hover:underline transition-all duration-200"
                >
                  Create your first project
                </button>
                {projectActionError && (
                  <p className="mt-2 text-xs text-red-600">{projectActionError}</p>
                )}
              </div>
            )}
          </div>
          )}
        </nav>

        {/* Settings at bottom */}
        <div className="relative p-2" ref={settingsRef}>
          <button
            onClick={() => isExpanded && setShowSettingsMenu(!showSettingsMenu)}
            className={`group w-full flex items-center rounded-lg text-sm transition-all duration-200 text-white/80 hover:bg-white/10 hover:text-white ${
              isExpanded ? 'justify-between gap-3 px-3 py-2' : 'justify-center p-2'
            } ${showSettingsMenu ? 'bg-white/15' : ''}`}
            title="Settings"
          >
            <div className={`flex items-center ${isExpanded ? 'gap-3' : ''}`}>
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <Settings className="w-6 h-6 text-white" />
              </div>
              {isExpanded && <span>Settings</span>}
            </div>
            {isExpanded && (
              <ChevronUp className={`w-4 h-4 transition-transform duration-300 ${showSettingsMenu ? '' : 'rotate-180'}`} />
            )}
          </button>

          {showSettingsMenu && isExpanded && (
            <div className="absolute bottom-full left-0 w-full mb-1 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200/50 animate-in fade-in slide-in-from-bottom-2 duration-200 py-1">
              {settingsMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      item.action?.();
                      setShowSettingsMenu(false);
                    }}
                    className={`group w-full flex items-center gap-3 px-3 py-2 text-sm transition-all duration-200 ${
                      item.id === 'logout' 
                        ? 'text-red-600 hover:bg-red-50 hover:scale-[1.02] hover:shadow-sm' 
                        : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900 hover:scale-[1.02] hover:shadow-sm'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        onProjectContinue={(project) => {
          onProjectContinue?.(project);
          setShowCreateProjectModal(false);
        }}
        onProjectCreated={handleProjectCreated}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        selectedCurrency={selectedCurrency}
        onCurrencyChange={(currency) => {
          if (onCurrencyChange) {
            onCurrencyChange(currency);
          }
        }}
        currentOrgId={userData?.org ?? undefined}
        userId={userData?.id ?? undefined}
        section={settingsModalSection}
      />

      {/* Edit Project Modal */}
      <EditProjectModal
        isOpen={showEditProjectModal}
        project={projectToEdit}
        onClose={() => {
          setShowEditProjectModal(false);
          setProjectToEdit(null);
        }}
        onProjectUpdated={handleProjectUpdated}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-slideDown">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Delete Project</h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-gray-900">
                  {deleteConfirm.project?.name}
                </span>
                ?
              </p>
              <p className="text-sm text-gray-500">
                This action will archive the project and all its blueprints. This cannot be undone.
              </p>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, project: null })}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <GatheringInfoToast
        message={gatheringInfoNotice}
        onClear={() => setGatheringInfoNotice(null)}
      />
    </>
  );
};

export default Sidebar;